<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { StoreSettingsService } from '@/services/store-settings.services';
import axiosInstance from '@/services/interceptor.services';
import StorefrontHomePreview from './StorefrontHomePreview.vue';
import ThemeContrastWarning from './ThemeContrastWarning.vue';
import ContrastConfirmModal from './ContrastConfirmModal.vue';
import CommerceCollectSlotsSettings from './CommerceCollectSlotsSettings.vue';
import CommerceOrderChangeSettings from './CommerceOrderChangeSettings.vue';

const props = defineProps<{
  storeId: string;
  moduleKey: string;
  storeName?: string;
  logoUrl?: string | null;
}>();
const emit = defineEmits<{
  (e: 'saved'): void;
  // Titre de la sous-vue plein écran actuellement ouverte (créneaux,
  // modification & annulation), ou null si aucune — permet au parent
  // (CommerceDetailPage) de remplacer son titre de fil d'Ariane au lieu
  // d'empiler un second header à l'intérieur de ce composant.
  (e: 'update:subViewTitle', title: string | null): void;
}>();

const { t } = useI18n({ useScope: 'global' });

// ── Types ────────────────────────────────────────────────────────────────────
interface SettingDef {
  key: string;
  category: string;
  type: 'tristate' | 'boolean';
  value: unknown;
  default: unknown;
  options?: string[];
  customerVisible: boolean;
  labelKey: string;
  descriptionKey: string;
}

interface ColorToken {
  light: string;
  dark: string | null;
}

// Palette de swatches prédéfinis (même esprit que /settings/theme) — un
// swatch "+" avec color picker caché permet toujours une couleur custom.
const SWATCH_COLORS = [
  '#0f62fe',
  '#111111',
  '#2e7d32',
  '#7c3aed',
  '#be123c',
  '#d97706',
];
// Fonds clairs neutres pour le mode clair, fonds sombres pour le mode sombre.
const BACKGROUND_SWATCH_COLORS = ['#ffffff', '#f8f9fa', '#f3f4f6', '#fbf8f2'];
const BACKGROUND_SWATCH_COLORS_DARK = [
  '#0f1419',
  '#111827',
  '#18181b',
  '#1a1a1a',
];
// Nuances de gris pour le texte produit (titre/description) — lisibles sur
// fond clair ou sombre selon la variante.
const TEXT_SWATCH_COLORS = ['#111111', '#333333', '#555555', '#888888'];
const TEXT_SWATCH_COLORS_DARK = ['#ffffff', '#e5e5e5', '#b0b0b0', '#888888'];

// ── Mapping module → clés de settings pertinentes ─────────────────────────────
// Seul Click & Collect expose un panneau riche (checkout + pickup + thème
// dédié) ; les autres modules n'ont pas encore de settings spécifiques côté
// API — leur panneau reste minimal (juste le on/off, déjà géré par la carte).
const MODULE_SETTING_KEYS: Record<string, string[]> = {
  'modules.click_and_collect': [
    'checkout.name',
    'checkout.email',
    'checkout.phone',
    'pickup.takeaway',
    'pickup.dine_in',
  ],
  'modules.pds_display': [],
  'modules.pos_register': [],
  'modules.kiosk': [],
};
const MODULE_THEME_PREFIX: Record<string, string> = {
  'modules.click_and_collect': 'theme.click_and_collect',
};
// Seul Click & Collect a une maquette de prévisualisation dédiée pour l'instant.
const MODULES_WITH_PREVIEW = new Set(['modules.click_and_collect']);

// labelKey/descriptionKey renvoyés par l'API pointent vers des clés i18n du
// storefront client (ex. "settings.checkout.name.label"), absentes des
// locales admin — et comme s.key contient lui-même des points
// (ex. "checkout.name"), on ne peut pas non plus la résoudre via un chemin
// i18n imbriqué. On maintient donc ici des libellés admin dédiés, indexés
// directement par s.key (clé plate, pas de résolution par point).
const SETTING_LABELS: Record<string, string> = {
  'checkout.name': 'moduleSettings.settingLabel.checkoutName',
  'checkout.email': 'moduleSettings.settingLabel.checkoutEmail',
  'checkout.phone': 'moduleSettings.settingLabel.checkoutPhone',
  'pickup.takeaway': 'moduleSettings.settingLabel.pickupTakeaway',
  'pickup.dine_in': 'moduleSettings.settingLabel.pickupDineIn',
};
const SETTING_DESCRIPTIONS: Record<string, string> = {
  'checkout.name': 'moduleSettings.settingDesc.checkoutName',
  'checkout.email': 'moduleSettings.settingDesc.checkoutEmail',
  'checkout.phone': 'moduleSettings.settingDesc.checkoutPhone',
  'pickup.takeaway': 'moduleSettings.settingDesc.pickupTakeaway',
  'pickup.dine_in': 'moduleSettings.settingDesc.pickupDineIn',
};
function settingLabel(s: SettingDef): string {
  const key = SETTING_LABELS[s.key];
  return key ? t(key) : s.key;
}
function settingDescription(s: SettingDef): string {
  const key = SETTING_DESCRIPTIONS[s.key];
  return key ? t(key) : '';
}

const relevantKeys = computed(() => MODULE_SETTING_KEYS[props.moduleKey] ?? []);
const themePrefix = computed(
  () => MODULE_THEME_PREFIX[props.moduleKey] ?? null,
);
const modulePreviewCapable = computed(() =>
  MODULES_WITH_PREVIEW.has(props.moduleKey),
);

// ── State ────────────────────────────────────────────────────────────────────
const loading = ref(true);
const loadError = ref<string | null>(null);
const saving = ref(false);
const saved = ref(false);
const saveError = ref<string | null>(null);

const allSettings = ref<SettingDef[]>([]);
const values = ref<Record<string, unknown>>({});
const brandPrimary = ref<ColorToken>({ light: '#0f62fe', dark: null });
const brandSecondary = ref<ColorToken>({ light: '#7c3aed', dark: null });
const backgroundColor = ref<ColorToken>({ light: '#f8f9fa', dark: '#0f1419' });
const productTitleColor = ref<ColorToken>({
  light: '#1a1a1a',
  dark: '#ffffff',
});
const productDescColor = ref<ColorToken>({ light: '#666666', dark: '#a0a0a0' });

// Applique la couleur suggérée par ThemeContrastWarning quand l'admin clique
// "Utiliser cette couleur" — remplace directement la valeur en échec, dans
// le même mode (clair/sombre) que celui où le contraste a été signalé.
function applyContrastSuggestion(
  target: 'foreground' | 'foregroundMuted',
  themeMode: 'light' | 'dark',
  color: string,
) {
  const colorRef =
    target === 'foreground' ? productTitleColor : productDescColor;
  colorRef.value = { ...colorRef.value, [themeMode]: color };
}

const themePreset = ref('orange');
// Bascule maître : si désactivée, le CNC utilise les valeurs par défaut de
// la plateforme et la section de personnalisation est masquée/désactivée.
const usePersonalization = ref(false);
const previewMode = ref<'light' | 'dark'>('light');
const previewDevice = ref<'mobile' | 'tablet' | 'desktop'>('mobile');

// ── Vrais produits du commerce, pour la prévisualisation ──────────────────────
interface PreviewProduct {
  name: string;
  price: string;
  imageUrl: string | null;
  description: string | null;
}
const previewProducts = ref<PreviewProduct[]>([]);
// Contrôle depuis le panneau "Paramètres de la carte produit" si le bouton +
// (ajout rapide au panier) apparaît sur les cartes produit du simulateur.
const showAddButton = ref(false);

async function loadPreviewProducts() {
  try {
    const res = await axiosInstance.get('store-products/search', {
      params: {
        storeId: props.storeId,
        pagination: true,
        page: 1,
        pageSize: 4,
      },
    });
    const list = res.data?.data ?? res.data ?? [];
    previewProducts.value = list.map((sp: any) => {
      const product = sp.product ?? sp;
      const price = sp.price ?? product.price ?? 0;
      const imageUrl =
        product.image?.info?.secureUrl ?? product.image?.url ?? null;
      return {
        name: product.designation ?? product.name ?? '',
        price: `${Number(price).toFixed(2).replace('.', ',')} €`,
        imageUrl,
        description: product.description ?? null,
      };
    });
  } catch {
    previewProducts.value = [];
  }
}

const moduleSettings = computed(() =>
  allSettings.value.filter((s) => relevantKeys.value.includes(s.key)),
);
// Masquées temporairement : "Commande" (checkout.*) et "Retrait" (pickup.*)
// ne sont pas encore fonctionnelles côté client. À retirer de cette liste
// une fois opérationnelles.
const HIDDEN_CATEGORIES = ['checkout', 'pickup'];
const settingsByCategory = computed(() => {
  const groups = new Map<string, SettingDef[]>();
  for (const s of moduleSettings.value) {
    if (HIDDEN_CATEGORIES.includes(s.category)) continue;
    if (!groups.has(s.category)) groups.set(s.category, []);
    groups.get(s.category)!.push(s);
  }
  return Array.from(groups.entries()).map(([category, settings]) => ({
    category,
    settings,
  }));
});

// ── Accordion des sections : une seule ouverte à la fois, "theme" par défaut ──
const openSectionKey = ref<string>('theme');
function toggleSection(key: string) {
  openSectionKey.value = openSectionKey.value === key ? '' : key;
}
const hasPreview = computed(() => modulePreviewCapable.value);

// ── Créneaux de collecte / Modification & annulation : vues plein écran
// séparées (comme la configuration paiement dans CommercePaymentsTab),
// pas des sections de l'accordéon ─────────────────────────────────────────
const collectSlotsOpen = ref(false);
const orderChangeOpen = ref(false);

watch([collectSlotsOpen, orderChangeOpen], () => {
  const title = collectSlotsOpen.value
    ? t('collectSlots.tabTitle')
    : orderChangeOpen.value
      ? t('orderChange.title')
      : null;
  emit('update:subViewTitle', title);
});

function closeSubView() {
  collectSlotsOpen.value = false;
  orderChangeOpen.value = false;
}
defineExpose({ closeSubView });

const previewPrimary = computed(() =>
  previewMode.value === 'dark'
    ? (brandPrimary.value.dark ?? brandPrimary.value.light)
    : brandPrimary.value.light,
);
const previewSecondary = computed(() =>
  previewMode.value === 'dark'
    ? (brandSecondary.value.dark ?? brandSecondary.value.light)
    : brandSecondary.value.light,
);
const previewBackground = computed(() =>
  previewMode.value === 'dark'
    ? (backgroundColor.value.dark ?? backgroundColor.value.light)
    : backgroundColor.value.light,
);
const previewTitleColor = computed(() =>
  previewMode.value === 'dark'
    ? (productTitleColor.value.dark ?? productTitleColor.value.light)
    : productTitleColor.value.light,
);
const previewDescColor = computed(() =>
  previewMode.value === 'dark'
    ? (productDescColor.value.dark ?? productDescColor.value.light)
    : productDescColor.value.light,
);
const previewTakeawayOn = computed(() => !!values.value['pickup.takeaway']);
const previewDineInOn = computed(() => !!values.value['pickup.dine_in']);
const previewPhoneRequired = computed(
  () => values.value['checkout.phone'] === 'required',
);

function categoryLabel(category: string): string {
  return t(`moduleSettings.category.${category}`, category);
}

// Snapshot de l'état tel que chargé depuis le serveur — permet de tout
// réinitialiser sans re-fetch (bouton "Réinitialiser").
const initialValues = ref<Record<string, unknown>>({});
const initialBrandPrimary = ref<ColorToken>({ light: '#0f62fe', dark: null });
const initialBrandSecondary = ref<ColorToken>({ light: '#7c3aed', dark: null });
const initialBackgroundColor = ref<ColorToken>({
  light: '#f8f9fa',
  dark: '#0f1419',
});
const initialProductTitleColor = ref<ColorToken>({
  light: '#1a1a1a',
  dark: '#ffffff',
});
const initialProductDescColor = ref<ColorToken>({
  light: '#666666',
  dark: '#a0a0a0',
});
const initialUsePersonalization = ref(false);
const initialShowAddButton = ref(false);
const initialThemePreset = ref('orange');

function resetToLoaded() {
  values.value = { ...initialValues.value };
  brandPrimary.value = { ...initialBrandPrimary.value };
  brandSecondary.value = { ...initialBrandSecondary.value };
  backgroundColor.value = { ...initialBackgroundColor.value };
  productTitleColor.value = { ...initialProductTitleColor.value };
  productDescColor.value = { ...initialProductDescColor.value };
  showAddButton.value = initialShowAddButton.value;
  usePersonalization.value = initialUsePersonalization.value;
  themePreset.value = initialThemePreset.value;
}

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    const res = await StoreSettingsService.getStoreSettings(props.storeId);
    const data = res.data?.data ?? res.data;
    const categories = data?.categories ?? [];
    const flat: SettingDef[] = [];
    for (const cat of categories) {
      for (const s of cat.settings ?? []) flat.push(s);
    }
    allSettings.value = flat;
    values.value = {};
    for (const s of flat) values.value[s.key] = s.value;

    if (themePrefix.value) {
      const themeRes = await StoreSettingsService.getStoreTheme(props.storeId);
      const theme = themeRes.data?.data ?? themeRes.data;
      const readColor = (key: string): ColorToken | null => {
        const light = theme?.light?.[key];
        return light ? { light, dark: theme?.dark?.[key] ?? null } : null;
      };
      brandPrimary.value = readColor('brand_primary') ?? brandPrimary.value;
      brandSecondary.value =
        readColor('brand_secondary') ?? brandSecondary.value;
      backgroundColor.value = readColor('background') ?? backgroundColor.value;
      productTitleColor.value =
        readColor('foreground') ?? productTitleColor.value;
      productDescColor.value =
        readColor('foreground_muted') ?? productDescColor.value;
      usePersonalization.value = !!theme?.enabled;
      themePreset.value = theme?.preset ?? themePreset.value;
    }
    showAddButton.value = !!(data?.['cart.quick_add'] ?? data?.cart?.quick_add);

    initialValues.value = { ...values.value };
    initialBrandPrimary.value = { ...brandPrimary.value };
    initialBrandSecondary.value = { ...brandSecondary.value };
    initialBackgroundColor.value = { ...backgroundColor.value };
    initialProductTitleColor.value = { ...productTitleColor.value };
    initialProductDescColor.value = { ...productDescColor.value };
    initialUsePersonalization.value = usePersonalization.value;
    initialShowAddButton.value = showAddButton.value;
    initialThemePreset.value = themePreset.value;
  } catch {
    loadError.value = t('moduleSettings.loadError');
  } finally {
    loading.value = false;
  }
}

const showContrastConfirm = ref(false);

function hexToLinear(hex: string): number[] | null {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!match) return null;
  return [match[1], match[2], match[3]].map((c) => {
    const v = parseInt(c, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
}

function relativeLuminance(hex: string): number | null {
  const rgb = hexToLinear(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(hexA: string, hexB: string): number | null {
  const lumA = relativeLuminance(hexA);
  const lumB = relativeLuminance(hexB);
  if (lumA === null || lumB === null) return null;
  const lighter = Math.max(lumA, lumB);
  const darker = Math.min(lumA, lumB);
  return (lighter + 0.05) / (darker + 0.05);
}

const WCAG_AA_TEXT = 4.5;
const WCAG_AA_MUTED = 3;

function hasInsufficientContrast(): boolean {
  const pairs: { bg: string | null; fg: string | null; min: number }[] = [
    {
      bg: backgroundColor.value.light,
      fg: productTitleColor.value.light,
      min: WCAG_AA_TEXT,
    },
    {
      bg: backgroundColor.value.dark ?? backgroundColor.value.light,
      fg: productTitleColor.value.dark ?? productTitleColor.value.light,
      min: WCAG_AA_TEXT,
    },
    {
      bg: backgroundColor.value.light,
      fg: productDescColor.value.light,
      min: WCAG_AA_MUTED,
    },
    {
      bg: backgroundColor.value.dark ?? backgroundColor.value.light,
      fg: productDescColor.value.dark ?? productDescColor.value.light,
      min: WCAG_AA_MUTED,
    },
  ];
  return pairs.some((pair) => {
    if (!pair.bg || !pair.fg) return false;
    const ratio = contrastRatio(pair.bg, pair.fg);
    return ratio !== null && ratio < pair.min;
  });
}

function save() {
  if (!themePrefix.value || !usePersonalization.value) {
    doSave();
    return;
  }
  if (hasInsufficientContrast()) {
    showContrastConfirm.value = true;
    return;
  }
  doSave();
}

function confirmSaveDespiteContrast() {
  showContrastConfirm.value = false;
  doSave();
}

async function doSave() {
  saving.value = true;
  saved.value = false;
  saveError.value = null;
  try {
    const payload: Record<string, unknown> = {};
    for (const key of relevantKeys.value) payload[key] = values.value[key];

    if (themePrefix.value) {
      const colorPayload = (token: ColorToken) => ({
        light: token.light,
        ...(token.dark ? { dark: token.dark } : {}),
      });
      const colorsByKey: Record<string, ColorToken> = {
        brand_primary: brandPrimary.value,
        brand_secondary: brandSecondary.value,
        background: backgroundColor.value,
        foreground: productTitleColor.value,
        foreground_muted: productDescColor.value,
      };
      for (const [key, token] of Object.entries(colorsByKey)) {
        payload[`theme.color.${key}`] = colorPayload(token);
        payload[`${themePrefix.value}.color.${key}`] = colorPayload(token);
      }
      payload['theme.enabled'] = usePersonalization.value;
      payload['theme.preset'] = themePreset.value;
    }
    payload['cart.quick_add'] = showAddButton.value;

    await StoreSettingsService.updateStoreSettings(
      props.storeId,
      payload,
      true,
    );
    saved.value = true;
    emit('saved');
    setTimeout(() => {
      saved.value = false;
    }, 2500);
  } catch (e: any) {
    const msg = e?.response?.data?.message;
    saveError.value = Array.isArray(msg)
      ? msg.join(' ')
      : (msg ?? t('moduleSettings.saveError'));
  } finally {
    saving.value = false;
  }
}

watch(
  () => props.moduleKey,
  () => load(),
);
onMounted(() => {
  load();
  loadPreviewProducts();
});
</script>

<template>
  <div v-if="collectSlotsOpen" class="mst mst__collectslots-view">
    <CommerceCollectSlotsSettings :store-id="storeId" />
  </div>

  <div v-else-if="orderChangeOpen" class="mst mst__collectslots-view">
    <CommerceOrderChangeSettings :store-id="storeId" />
  </div>

  <div v-else class="mst" :class="{ 'mst--with-preview': hasPreview }">
    <!-- ── Panneau gauche : réglages (scrollable) ──────────────────────────── -->
    <div class="mst__panel">
      <div v-if="loading" class="mst__loading">
        <i class="pi pi-spin pi-spinner" />
        {{ t('moduleSettings.loading') }}
      </div>

      <div v-else-if="loadError" class="mst__error">
        <i class="pi pi-exclamation-triangle" />
        {{ loadError }}
      </div>

      <template v-else>
        <div v-if="themePrefix" class="mst__master-row">
          <div class="mst__field-label-wrap">
            <span class="mst__field-label">{{
              t('moduleSettings.usePersonalization')
            }}</span>
            <span class="mst__field-desc">{{
              t('moduleSettings.usePersonalizationDesc')
            }}</span>
          </div>
          <label class="mst__toggle">
            <input
              type="checkbox"
              class="mst__toggle-input"
              :checked="usePersonalization"
              @change="usePersonalization = !usePersonalization"
            />
            <span class="mst__toggle-track" />
          </label>
        </div>

        <button
          v-if="moduleKey === 'modules.click_and_collect'"
          type="button"
          class="mst__collectslots-btn"
          @click="collectSlotsOpen = true"
        >
          <i class="pi pi-calendar-plus" />
          {{ t('collectSlots.tabTitle') }}
          <span class="mst__collectslots-badge">{{
            t('collectSlots.badgeExample')
          }}</span>
          <i class="pi pi-chevron-right" style="margin-left: auto" />
        </button>

        <button
          v-if="moduleKey === 'modules.click_and_collect'"
          type="button"
          class="mst__collectslots-btn"
          @click="orderChangeOpen = true"
        >
          <i class="pi pi-pencil" />
          {{ t('orderChange.title') }}
          <i class="pi pi-chevron-right" style="margin-left: auto" />
        </button>

        <div v-if="!moduleSettings.length && !themePrefix" class="mst__empty">
          <i class="pi pi-sliders-h" />
          <p>{{ t('moduleSettings.noSettings') }}</p>
        </div>

        <template v-else>
          <section
            v-for="group in settingsByCategory"
            :key="group.category"
            class="mst__section"
            :class="{ 'mst__section--open': openSectionKey === group.category }"
          >
            <button
              type="button"
              class="mst__section-head"
              @click="toggleSection(group.category)"
            >
              <i class="pi pi-sliders-h mst__section-icon" />
              <h4 class="mst__section-title">
                {{ categoryLabel(group.category) }}
              </h4>
              <i
                class="pi pi-chevron-down mst__section-chevron"
                :class="{
                  'mst__section-chevron--open':
                    openSectionKey === group.category,
                }"
              />
            </button>
            <div
              v-show="openSectionKey === group.category"
              class="mst__field-list mst__field-list--inline"
            >
              <div
                v-for="s in group.settings"
                :key="s.key"
                class="mst__field mst__field--inline"
              >
                <div class="mst__field-label-wrap">
                  <span class="mst__field-label">{{ settingLabel(s) }}</span>
                  <span class="mst__field-desc">{{
                    settingDescription(s)
                  }}</span>
                </div>

                <select
                  v-if="s.type === 'tristate'"
                  v-model="values[s.key]"
                  class="mst__select"
                >
                  <option v-for="opt in s.options" :key="opt" :value="opt">
                    {{ t(`moduleSettings.tristate.${opt}`, opt) }}
                  </option>
                </select>

                <label v-else class="mst__toggle">
                  <input
                    type="checkbox"
                    class="mst__toggle-input"
                    :checked="!!values[s.key]"
                    @change="values[s.key] = !values[s.key]"
                  />
                  <span class="mst__toggle-track" />
                </label>
              </div>
            </div>
          </section>

          <section
            v-if="themePrefix && usePersonalization"
            class="mst__section"
            :class="{ 'mst__section--open': openSectionKey === 'theme' }"
          >
            <button
              type="button"
              class="mst__section-head"
              @click="toggleSection('theme')"
            >
              <i class="pi pi-palette mst__section-icon" />
              <h4 class="mst__section-title">
                {{ t('moduleSettings.brandColors') }}
              </h4>
              <i
                class="pi pi-chevron-down mst__section-chevron"
                :class="{
                  'mst__section-chevron--open': openSectionKey === 'theme',
                }"
              />
            </button>

            <div v-show="openSectionKey === 'theme'" class="mst__field-list">
              <div v-if="previewMode === 'light'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.primaryColorLight')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in SWATCH_COLORS"
                    :key="c"
                    class="mst__swatch"
                    :class="{ 'mst__swatch--active': brandPrimary.light === c }"
                    :style="{ background: c }"
                    @click="brandPrimary.light = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="brandPrimary.light"
                      @input="
                        brandPrimary.light = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
              <div v-if="previewMode === 'dark'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.primaryColorDark')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in SWATCH_COLORS"
                    :key="c"
                    class="mst__swatch"
                    :class="{ 'mst__swatch--active': brandPrimary.dark === c }"
                    :style="{ background: c }"
                    @click="brandPrimary.dark = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="brandPrimary.dark ?? brandPrimary.light"
                      @input="
                        brandPrimary.dark = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>

              <div v-if="previewMode === 'light'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.secondaryColorLight')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in SWATCH_COLORS"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': brandSecondary.light === c,
                    }"
                    :style="{ background: c }"
                    @click="brandSecondary.light = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="brandSecondary.light"
                      @input="
                        brandSecondary.light = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
              <div v-if="previewMode === 'dark'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.secondaryColorDark')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in SWATCH_COLORS"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': brandSecondary.dark === c,
                    }"
                    :style="{ background: c }"
                    @click="brandSecondary.dark = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="brandSecondary.dark ?? brandSecondary.light"
                      @input="
                        brandSecondary.dark = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>

          <section
            v-if="themePrefix && usePersonalization"
            class="mst__section"
            :class="{ 'mst__section--open': openSectionKey === 'background' }"
          >
            <button
              type="button"
              class="mst__section-head"
              @click="toggleSection('background')"
            >
              <i class="pi pi-image mst__section-icon" />
              <h4 class="mst__section-title">
                {{ t('moduleSettings.backgroundColors') }}
              </h4>
              <i
                class="pi pi-chevron-down mst__section-chevron"
                :class="{
                  'mst__section-chevron--open': openSectionKey === 'background',
                }"
              />
            </button>

            <div
              v-show="openSectionKey === 'background'"
              class="mst__field-list"
            >
              <div v-if="previewMode === 'light'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.backgroundLight')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in BACKGROUND_SWATCH_COLORS"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': backgroundColor.light === c,
                    }"
                    :style="{ background: c }"
                    @click="backgroundColor.light = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="backgroundColor.light"
                      @input="
                        backgroundColor.light = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
              <div v-if="previewMode === 'dark'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.backgroundDark')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in BACKGROUND_SWATCH_COLORS_DARK"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': backgroundColor.dark === c,
                    }"
                    :style="{ background: c }"
                    @click="backgroundColor.dark = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="backgroundColor.dark ?? backgroundColor.light"
                      @input="
                        backgroundColor.dark = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>

          <section
            v-if="themePrefix && usePersonalization"
            class="mst__section"
            :class="{ 'mst__section--open': openSectionKey === 'productCard' }"
          >
            <button
              type="button"
              class="mst__section-head"
              @click="toggleSection('productCard')"
            >
              <i class="pi pi-th-large mst__section-icon" />
              <h4 class="mst__section-title">
                {{ t('moduleSettings.productCardSettings') }}
              </h4>
              <i
                class="pi pi-chevron-down mst__section-chevron"
                :class="{
                  'mst__section-chevron--open':
                    openSectionKey === 'productCard',
                }"
              />
            </button>

            <div
              v-show="openSectionKey === 'productCard'"
              class="mst__field-list"
            >
              <div class="mst__field">
                <div class="mst__field-label-wrap">
                  <span class="mst__field-label">{{
                    t('moduleSettings.showAddButton')
                  }}</span>
                  <span class="mst__field-desc">{{
                    t('moduleSettings.showAddButtonDesc')
                  }}</span>
                </div>
                <label class="mst__toggle">
                  <input
                    type="checkbox"
                    class="mst__toggle-input"
                    :checked="showAddButton"
                    @change="showAddButton = !showAddButton"
                  />
                  <span class="mst__toggle-track" />
                </label>
              </div>
            </div>
          </section>

          <section
            v-if="themePrefix && usePersonalization"
            class="mst__section"
            :class="{ 'mst__section--open': openSectionKey === 'productText' }"
          >
            <button
              type="button"
              class="mst__section-head"
              @click="toggleSection('productText')"
            >
              <i class="pi pi-font mst__section-icon" />
              <h4 class="mst__section-title">
                {{ t('moduleSettings.productTextColors') }}
              </h4>
              <i
                class="pi pi-chevron-down mst__section-chevron"
                :class="{
                  'mst__section-chevron--open':
                    openSectionKey === 'productText',
                }"
              />
            </button>

            <div
              v-show="openSectionKey === 'productText'"
              class="mst__field-list"
            >
              <div v-if="previewMode === 'light'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.titleColorLight')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in TEXT_SWATCH_COLORS"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': productTitleColor.light === c,
                    }"
                    :style="{ background: c }"
                    @click="productTitleColor.light = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="productTitleColor.light"
                      @input="
                        productTitleColor.light = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
              <div v-if="previewMode === 'dark'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.titleColorDark')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in TEXT_SWATCH_COLORS_DARK"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': productTitleColor.dark === c,
                    }"
                    :style="{ background: c }"
                    @click="productTitleColor.dark = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="productTitleColor.dark ?? productTitleColor.light"
                      @input="
                        productTitleColor.dark = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>

              <div v-if="previewMode === 'light'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.descColorLight')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in TEXT_SWATCH_COLORS"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': productDescColor.light === c,
                    }"
                    :style="{ background: c }"
                    @click="productDescColor.light = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="productDescColor.light"
                      @input="
                        productDescColor.light = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
              <div v-if="previewMode === 'dark'" class="mst__color-field-wrap">
                <label class="mst__label">{{
                  t('moduleSettings.descColorDark')
                }}</label>
                <div class="mst__swatches">
                  <button
                    v-for="c in TEXT_SWATCH_COLORS_DARK"
                    :key="c"
                    class="mst__swatch"
                    :class="{
                      'mst__swatch--active': productDescColor.dark === c,
                    }"
                    :style="{ background: c }"
                    @click="productDescColor.dark = c"
                  />
                  <label
                    class="mst__swatch mst__swatch--add"
                    :title="t('moduleSettings.customColor')"
                  >
                    <i
                      class="pi pi-plus"
                      style="font-size: 0.75rem; color: #555"
                    />
                    <input
                      type="color"
                      class="mst__color-input"
                      :value="productDescColor.dark ?? productDescColor.light"
                      @input="
                        productDescColor.dark = (
                          $event.target as HTMLInputElement
                        ).value
                      "
                    />
                  </label>
                </div>
              </div>
            </div>
          </section>
        </template>

        <ThemeContrastWarning
          v-if="themePrefix && usePersonalization"
          :background="backgroundColor"
          :foreground="productTitleColor"
          :foreground-muted="productDescColor"
          @apply-suggestion="applyContrastSuggestion"
        />

        <div v-if="saveError" class="mst__error">
          <i class="pi pi-exclamation-triangle" />
          <span>{{ saveError }}</span>
          <button class="mst__dismiss-btn" @click="saveError = null">
            <i class="pi pi-times" />
          </button>
        </div>
        <div v-if="saved" class="mst__success">
          <i class="pi pi-check-circle" />
          {{ t('moduleSettings.saved') }}
        </div>

        <div class="mst__actions">
          <button
            class="mst__btn mst__btn--ghost"
            :disabled="saving"
            @click="resetToLoaded"
          >
            <i class="pi pi-refresh" />
            {{ t('moduleSettings.reset') }}
          </button>
          <button
            class="mst__btn mst__btn--primary"
            :disabled="saving"
            @click="save"
          >
            <i v-if="saving" class="pi pi-spin pi-spinner" />
            <i v-else-if="saved" class="pi pi-check" />
            <i v-else class="pi pi-save" />
            {{ saving ? t('moduleSettings.saving') : t('moduleSettings.save') }}
          </button>
        </div>
      </template>
    </div>

    <!-- ── Colonne droite : prévisualisation live (fixe) ───────────────────── -->
    <div v-if="hasPreview" class="mst__preview-col">
      <div class="mst__preview-label">
        <span>{{ t('moduleSettings.livePreview') }}</span>
        <div class="mst__preview-modes">
          <button
            class="mst__preview-mode"
            :class="{ 'mst__preview-mode--active': previewMode === 'light' }"
            @click="previewMode = 'light'"
          >
            <i class="pi pi-sun" />
          </button>
          <button
            class="mst__preview-mode"
            :class="{ 'mst__preview-mode--active': previewMode === 'dark' }"
            @click="previewMode = 'dark'"
          >
            <i class="pi pi-moon" />
          </button>
          <span class="mst__preview-sep" />
          <button
            class="mst__preview-mode"
            :class="{ 'mst__preview-mode--active': previewDevice === 'mobile' }"
            :title="t('storefrontPreview.mobile')"
            @click="previewDevice = 'mobile'"
          >
            <i class="pi pi-mobile" />
          </button>
          <button
            class="mst__preview-mode"
            :class="{ 'mst__preview-mode--active': previewDevice === 'tablet' }"
            :title="t('storefrontPreview.tablet')"
            @click="previewDevice = 'tablet'"
          >
            <i class="pi pi-tablet" />
          </button>
          <button
            class="mst__preview-mode"
            :class="{
              'mst__preview-mode--active': previewDevice === 'desktop',
            }"
            :title="t('storefrontPreview.desktop')"
            @click="previewDevice = 'desktop'"
          >
            <i class="pi pi-desktop" />
          </button>
        </div>
      </div>

      <div class="mst__canvas mst__canvas--row">
        <!-- Halos déco -->
        <div
          class="mst__halo mst__halo--tr"
          :style="{ background: previewPrimary + '22' }"
        />
        <div
          class="mst__halo mst__halo--bl"
          :style="{ background: previewPrimary + '11' }"
        />

        <!-- Carte preview (même design que la carte "Nextep Pay" de /settings/theme) —
             toujours visible, quel que soit le mode du simulateur à côté. -->
        <div class="mst__card mst__card--compact">
          <div class="mst__card-head">
            <div>
              <h4 class="mst__card-title" :style="{ color: previewPrimary }">
                Click &amp; Collect
              </h4>
              <p class="mst__card-sub">
                {{ t('moduleSettings.previewSubtitle') }}
              </p>
            </div>
            <div class="mst__card-icon" :style="{ background: previewPrimary }">
              <i
                class="pi pi-shopping-cart"
                style="color: #fff; font-size: 1.125rem"
              />
            </div>
          </div>

          <div class="mst__card-kpis">
            <div
              class="mst__kpi"
              :class="{ 'mst__kpi--on': previewTakeawayOn }"
              :style="
                previewTakeawayOn ? { borderColor: previewPrimary + '55' } : {}
              "
            >
              <p class="mst__kpi-label">
                {{ t('moduleSettings.previewTakeaway') }}
              </p>
              <p class="mst__kpi-value">
                <i
                  class="pi"
                  :class="
                    previewTakeawayOn ? 'pi-check-circle' : 'pi-times-circle'
                  "
                  :style="{
                    color: previewTakeawayOn ? previewPrimary : '#94a3b8',
                  }"
                />
              </p>
              <span
                class="mst__kpi-chip"
                :style="
                  previewTakeawayOn
                    ? {
                        background: previewPrimary + '18',
                        color: previewPrimary,
                      }
                    : {}
                "
              >
                {{
                  previewTakeawayOn
                    ? t('moduleSettings.previewEnabled')
                    : t('moduleSettings.previewDisabled')
                }}
              </span>
            </div>
            <div
              class="mst__kpi"
              :class="{ 'mst__kpi--on': previewDineInOn }"
              :style="
                previewDineInOn ? { borderColor: previewPrimary + '55' } : {}
              "
            >
              <p class="mst__kpi-label">
                {{ t('moduleSettings.previewDineIn') }}
              </p>
              <p class="mst__kpi-value">
                <i
                  class="pi"
                  :class="
                    previewDineInOn ? 'pi-check-circle' : 'pi-times-circle'
                  "
                  :style="{
                    color: previewDineInOn ? previewPrimary : '#94a3b8',
                  }"
                />
              </p>
              <span
                class="mst__kpi-chip"
                :style="
                  previewDineInOn
                    ? {
                        background: previewPrimary + '18',
                        color: previewPrimary,
                      }
                    : {}
                "
              >
                {{
                  previewDineInOn
                    ? t('moduleSettings.previewEnabled')
                    : t('moduleSettings.previewDisabled')
                }}
              </span>
            </div>
          </div>

          <div class="mst__card-rows">
            <div class="mst__row">
              <div class="mst__row-left">
                <div class="mst__row-icon" style="background: #111">
                  <i
                    class="pi pi-user"
                    style="color: #fff; font-size: 0.75rem"
                  />
                </div>
                <span>{{ t('moduleSettings.previewNameField') }}</span>
              </div>
              <span class="mst__row-amount">{{
                t('moduleSettings.previewRequired')
              }}</span>
            </div>
            <div class="mst__row">
              <div class="mst__row-left">
                <div
                  class="mst__row-icon"
                  :style="{ background: previewPrimary }"
                >
                  <i
                    class="pi pi-phone"
                    style="color: #fff; font-size: 0.75rem"
                  />
                </div>
                <span>{{ t('moduleSettings.previewPhoneField') }}</span>
              </div>
              <span class="mst__row-amount">
                {{
                  previewPhoneRequired
                    ? t('moduleSettings.previewRequired')
                    : t('moduleSettings.previewOptional')
                }}
              </span>
            </div>
          </div>

          <div class="mst__card-actions">
            <button
              class="mst__action-btn mst__action-btn--primary"
              :style="{ background: previewSecondary }"
            >
              {{ t('moduleSettings.previewCta') }}
            </button>
            <button
              class="mst__action-btn mst__action-btn--outline"
              :style="{ borderColor: previewPrimary, color: previewPrimary }"
            >
              {{ t('moduleSettings.previewSecondaryCta') }}
            </button>
          </div>

          <!-- Notif flottante -->
          <div class="mst__notif">
            <div class="mst__notif-icon">
              <i
                class="pi pi-check-circle"
                style="color: #16a34a; font-size: 1rem"
              />
            </div>
            <div>
              <p class="mst__notif-title">
                {{ t('moduleSettings.previewNotifTitle') }}
              </p>
              <p class="mst__notif-sub">
                {{ t('moduleSettings.previewNotifSub') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Simulateur de la page d'accueil client (mobile/desktop) -->
        <StorefrontHomePreview
          v-model:device="previewDevice"
          :primary-color="previewPrimary"
          :secondary-color="previewSecondary"
          :background-color="previewBackground"
          :store-name="storeName"
          :logo-url="logoUrl"
          :products="previewProducts"
          :show-add-button="showAddButton"
          :title-color="previewTitleColor"
          :desc-color="previewDescColor"
        />
      </div>
    </div>

    <ContrastConfirmModal
      :open="showContrastConfirm"
      @confirm="confirmSaveDespiteContrast"
      @cancel="showContrastConfirm = false"
    />
  </div>
</template>

<style scoped>
.mst {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-height: 0;
}
.mst--with-preview {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: start;
}
@media (min-width: 1100px) {
  .mst--with-preview {
    grid-template-columns: minmax(0, 340px) 1fr;
  }
}

/* ── Panneau gauche scrollable ── */
.mst__panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 0;
}
@media (min-width: 1100px) {
  .mst--with-preview .mst__panel {
    max-height: calc(100vh - 14rem);
    overflow-y: auto;
    padding-right: 0.5rem;
  }
}

.mst__loading,
.mst__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2.5rem 1rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
  font-size: 0.875rem;
}
.mst__empty i,
.mst__loading i {
  font-size: 1.5rem;
  opacity: 0.5;
}

.mst__section {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 1.125rem 1.25rem;
  border-radius: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.mst__section--open {
  gap: 0.875rem;
}
.mst__section-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}
.mst__section-icon {
  font-size: 0.9rem;
  color: var(--nxt-on-surface-variant);
}
.mst__section-title {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--nxt-on-surface-variant);
}
.mst__section-chevron {
  margin-left: auto;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
  transition: transform 0.2s ease;
}
.mst__section-chevron--open {
  transform: rotate(180deg);
}

/* ── Bouton "Créneaux de collecte" + vue plein écran séparée ── */
.mst__collectslots-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem 1.125rem;
  border-radius: 1.125rem;
  border: 1.5px dashed var(--nxt-outline-variant);
  background: rgba(255, 214, 10, 0.06);
  color: var(--nxt-on-surface);
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
}
.mst__collectslots-btn:hover {
  border-color: #ffd60a;
}
.mst__collectslots-badge {
  font-size: 0.5625rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.1rem 0.4rem;
  border-radius: 9999px;
  background: rgba(255, 214, 10, 0.2);
  color: #8a6d00;
}
[data-theme='dark'] .mst__collectslots-badge {
  color: #ffd60a;
}
.mst__collectslots-view {
  gap: 1rem;
}

.mst__field-list {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}
.mst__field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.06));
}
.mst__field:first-child {
  padding-top: 0;
  border-top: none;
}
.mst__master-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
[data-theme='dark'] .mst__master-row {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}

/* Retrait / Commande : champs alignés en ligne, plusieurs par rangée */
.mst__field-list--inline {
  flex-direction: row;
  flex-wrap: wrap;
  gap: 1.25rem;
}
.mst__field--inline {
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.5rem;
  padding-top: 0;
  border-top: none;
  flex: 1 1 12rem;
  min-width: 10rem;
}

.mst__field-label-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
.mst__field-label {
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.mst__field-desc {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.mst__field-hint {
  margin: 0;
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted, #94a3b8);
}
.mst__select {
  height: 2.25rem;
  padding: 0 0.75rem;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-family: inherit;
  background: var(--nxt-card-bg, #fff);
  color: var(--nxt-on-surface);
  cursor: pointer;
  flex-shrink: 0;
}

/* Toggle (même pattern que ThemeSettingsPage) */
.mst__toggle {
  position: relative;
  flex-shrink: 0;
}
.mst__toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.mst__toggle-track {
  display: block;
  width: 2.625rem;
  height: 1.5rem;
  background: var(--nxt-surface-highest, #e1e3e4);
  border-radius: 9999px;
  cursor: pointer;
  transition: background 0.2s ease;
  position: relative;
}
.mst__toggle-track::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 1.25rem;
  height: 1.25rem;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.mst__toggle-input:checked ~ .mst__toggle-track {
  background: #10b981;
}
.mst__toggle-input:checked ~ .mst__toggle-track::after {
  transform: translateX(1.125rem);
}

/* Champs de couleur (swatches, même pattern que /settings/theme) */
.mst__color-field-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.mst__label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nxt-on-surface-variant);
}
.mst__swatches {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}
.mst__swatch {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid transparent;
  cursor: pointer;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;
  outline: none;
}
.mst__swatch:hover {
  transform: scale(1.12);
}
.mst__swatch--active {
  box-shadow:
    0 0 0 2px #fff,
    0 0 0 4px #141c29;
}
.mst__swatch--add {
  background: var(--nxt-surface-high, #e7e8e9);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.mst__color-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
  width: 100%;
  height: 100%;
  border: none;
  padding: 0;
}
[data-theme='dark'] .mst__swatch--active {
  box-shadow:
    0 0 0 2px #0b1320,
    0 0 0 4px #ffd60a;
}

.mst__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.09);
  color: #dc2626;
  font-size: 0.8125rem;
  font-weight: 600;
}
.mst__error span {
  flex: 1;
}
.mst__dismiss-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: inherit;
  cursor: pointer;
  flex-shrink: 0;
  opacity: 0.7;
}
.mst__dismiss-btn:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}
.mst__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border-radius: 0.625rem;
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  font-size: 0.8125rem;
  font-weight: 600;
}

.mst__actions {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}
.mst__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 2.25rem;
  padding: 0 1rem;
  border-radius: 0.5rem;
  border: 1.5px solid transparent;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
}
.mst__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.mst__btn--primary {
  background: var(--nxt-on-surface, #111);
  color: #fff;
  border-color: var(--nxt-on-surface, #111);
}
.mst__btn--primary:hover:not(:disabled) {
  opacity: 0.88;
}
.mst__btn--ghost {
  background: transparent;
  color: var(--nxt-on-surface-variant);
  border-color: var(--nxt-outline-variant, #e0e0e0);
}
.mst__btn--ghost:hover:not(:disabled) {
  background: var(--nxt-surface-low);
}
[data-theme='dark'] .mst__btn--ghost {
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
[data-theme='dark'] .mst__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  border-color: #ffd60a;
}
[data-theme='dark'] .mst__section {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .mst__collectslots-btn {
  background: rgba(255, 214, 10, 0.08);
}

/* ── Colonne droite : preview ── */
.mst__preview-col {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}
@media (min-width: 1100px) {
  .mst__preview-col {
    position: sticky;
    top: 0;
  }
}
.mst__preview-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--nxt-on-surface-variant);
}
.mst__preview-modes {
  display: flex;
  gap: 0.25rem;
}
.mst__preview-mode {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  border: none;
  background: transparent;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
  font-size: 0.875rem;
  transition: background 0.15s ease;
}
.mst__preview-mode:hover {
  background: var(--nxt-surface-high, #e7e8e9);
}
.mst__preview-mode--active {
  background: var(--nxt-surface-highest, #e1e3e4);
  color: var(--nxt-on-surface);
}

.mst__canvas {
  min-height: 480px;
  border-radius: 1.5rem;
  border: 1px solid var(--nxt-outline-variant, rgba(0, 0, 0, 0.08));
  background-image: radial-gradient(
    circle at 2px 2px,
    rgba(0, 0, 0, 0.04) 1px,
    transparent 0
  );
  background-size: 24px 24px;
  background-color: var(--nxt-surface-low, #f8f9fa);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}
.mst__canvas--row {
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: nowrap;
  padding: 1.5rem;
  overflow-x: auto;
  overflow-y: hidden;
}
.mst__preview-sep {
  width: 1px;
  height: 1.25rem;
  background: var(--nxt-outline-variant, rgba(0, 0, 0, 0.12));
  margin: 0 0.25rem;
}
.mst__halo {
  position: absolute;
  width: 24rem;
  height: 24rem;
  border-radius: 50%;
  filter: blur(100px);
  pointer-events: none;
}
.mst__halo--tr {
  top: -8rem;
  right: -8rem;
}
.mst__halo--bl {
  bottom: -8rem;
  left: -8rem;
}

/* Carte preview (design identique à /settings/theme) */
.mst__card {
  width: 100%;
  max-width: 520px;
  border-radius: 1.5rem;
  padding: 1.75rem;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  flex-shrink: 0;
}
.mst__card--compact {
  max-width: 300px;
  padding: 1.25rem;
  gap: 1rem;
}

.mst__card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}
.mst__card-title {
  margin: 0 0 0.25rem;
  font-size: 1.375rem;
  font-weight: 800;
}
.mst__card-sub {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
}
.mst__card-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.mst__card-kpis {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
}
.mst__kpi {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 0.875rem;
  padding: 0.875rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  transition: border-color 0.15s ease;
}
.mst__kpi--on {
  background: rgba(255, 255, 255, 0.5);
}
.mst__kpi-label {
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #666;
  margin: 0;
}
.mst__kpi-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: #111;
  margin: 0;
}
.mst__kpi-chip {
  display: inline-block;
  width: fit-content;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 9999px;
  background: rgba(0, 0, 0, 0.06);
  color: #666;
}

.mst__card-rows {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.mst__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.875rem;
  font-weight: 600;
}
.mst__row:last-child {
  border-bottom: none;
}
.mst__row-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.mst__row-icon {
  width: 2rem;
  height: 2rem;
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
.mst__row-amount {
  font-weight: 700;
  font-size: 0.75rem;
  color: #666;
}

.mst__card-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.mst__action-btn {
  width: 100%;
  height: 2.75rem;
  padding: 0 1rem;
  border-radius: 0.75rem;
  border: none;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  box-sizing: border-box;
  transition: filter 0.15s ease;
}
.mst__action-btn:hover {
  filter: brightness(1.08);
}
.mst__action-btn--primary {
  color: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.mst__action-btn--outline {
  background: transparent;
  border: 2px solid;
}

/* Notif flottante */
.mst__notif {
  position: absolute;
  bottom: 1.25rem;
  right: 1.25rem;
  background: #fff;
  border-radius: 0.875rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  animation: mst-bounce 2s infinite;
}
.mst__notif-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: #dcfce7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.mst__notif-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #111;
  margin: 0;
}
.mst__notif-sub {
  font-size: 0.6875rem;
  color: #666;
  margin: 0.1rem 0 0;
}
@keyframes mst-bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}
</style>
